import { useEffect, useState } from 'react';
import { Building2, Loader2, LogOut } from 'lucide-react';
import { listOrgs } from '@/api.ts';
import { Alert, AlertDescription } from '@/components/ui/alert.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { EmptyState } from '@/components/ui/empty-state.tsx';
import { useSession } from '@/session.tsx';
import type { OrgSummary } from '@/session.tsx';

/**
 * The step phase 2 did not have: signed in, but not yet pointed at an org.
 *
 * A console session names a person, and a person may belong to several orgs or to none. Asking is
 * the only honest thing to do — the phase-2 form got an org id because it made the human supply
 * one, which is exactly what signing in is supposed to replace.
 *
 * One org auto-selects. It is not a choice, and rendering a list of one so somebody can click the
 * only item in it is ceremony.
 */
export function ChooseOrg() {
  const { credential, selectOrg, signOut } = useSession();
  const [orgs, setOrgs] = useState<OrgSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const token = credential?.token;

  useEffect(() => {
    if (!token) return;
    let live = true;

    void listOrgs(token)
      .then((items) => {
        if (!live) return;
        if (items.length === 1) selectOrg(items[0]!);
        else setOrgs(items);
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : String(cause));
      });

    return () => { live = false; };
  }, [token, selectOrg]);

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-10">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Choose an organization</CardTitle>
          <CardDescription>
            {credential?.email
              ? <>Signed in as <span className="font-mono">{credential.email}</span>.</>
              : 'Signed in.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="grid gap-3">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!orgs && !error && (
            <div className="text-muted-foreground flex items-center gap-2 py-6 text-sm">
              <Loader2 className="size-4 animate-spin" />
              Loading your organizations…
            </div>
          )}

          {orgs?.length === 0 && (
            <EmptyState
              icon={<Building2 />}
              title="No organizations yet"
              description="This account is not a member of any organization. Ask an owner to add your email address, and sign in again."
            />
          )}

          {orgs?.map((org) => (
            <button
              key={org.orgId}
              type="button"
              onClick={() => selectOrg(org)}
              className="hover:bg-accent flex items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors"
            >
              <Building2 className="text-muted-foreground size-4 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{org.name}</span>
                <span className="text-muted-foreground block truncate font-mono text-xs">{org.slug}</span>
              </span>
              <Badge variant={org.role === 'viewer' ? 'secondary' : 'default'}>{org.role}</Badge>
            </button>
          ))}

          <Button variant="ghost" size="sm" className="justify-self-start" onClick={signOut}>
            <LogOut />
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
